
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO, isSameDay, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

interface AppointmentDay {
  date: Date;
  count: number;
  hasConfirmed: boolean;
}

interface AppointmentCalendarProps {
  psychologistId?: string;
  onDateSelect?: (date: Date) => void;
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({ 
  psychologistId,
  onDateSelect 
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [appointmentDays, setAppointmentDays] = useState<AppointmentDay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!psychologistId) {
      toast.error("ID do psicólogo não encontrado");
      setLoading(false);
      return;
    }

    async function loadAppointmentDays() {
      try {
        setLoading(true);
        
        // Primeiro dia do mês
        const firstDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
        // Último dia do mês
        const lastDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
        
        const startDate = format(firstDay, 'yyyy-MM-dd');
        const endDate = format(lastDay, 'yyyy-MM-dd');

        // Buscar todas as consultas do mês
        const { data, error } = await supabase
          .from("appointments")
          .select("date, status")
          .eq("psychologist_id", psychologistId)
          .gte("date", startDate)
          .lte("date", endDate);

        if (error) {
          console.error("Erro ao carregar consultas:", error);
          toast.error("Erro ao carregar dados do calendário");
          return;
        }

        // Agrupar consultas por dia
        const appointmentsByDay: Record<string, { count: number, hasConfirmed: boolean }> = {};
        
        data?.forEach(appointment => {
          if (!appointmentsByDay[appointment.date]) {
            appointmentsByDay[appointment.date] = { count: 0, hasConfirmed: false };
          }
          
          appointmentsByDay[appointment.date].count += 1;
          
          if (appointment.status === 'confirmed') {
            appointmentsByDay[appointment.date].hasConfirmed = true;
          }
        });

        // Converter para o formato esperado pelo componente
        const days: AppointmentDay[] = Object.entries(appointmentsByDay).map(([dateStr, info]) => ({
          date: parseISO(dateStr),
          count: info.count,
          hasConfirmed: info.hasConfirmed
        }));

        setAppointmentDays(days);
      } catch (error) {
        console.error("Erro inesperado:", error);
        toast.error("Erro inesperado ao carregar calendário");
      } finally {
        setLoading(false);
      }
    }

    loadAppointmentDays();
  }, [psychologistId, calendarMonth]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date && onDateSelect) {
      onDateSelect(date);
    }
  };

  const handlePreviousMonth = () => {
    setCalendarMonth(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCalendarMonth(prev => addMonths(prev, 1));
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2" />
            Calendário de Consultas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2" />
            Calendário de Consultas
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePreviousMonth}
              className="h-7 w-7"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm font-medium">
              {format(calendarMonth, 'MMMM yyyy', { locale: ptBR })}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleNextMonth}
              className="h-7 w-7"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          month={calendarMonth}
          onMonthChange={setCalendarMonth}
          locale={ptBR}
          modifiers={{
            hasAppointments: (day) => appointmentDays.some(d => isSameDay(d.date, day))
          }}
          modifiersStyles={{
            hasAppointments: { position: 'relative' }
          }}
          className="rounded-md border"
        />
        
        <div className="mt-4 flex items-center justify-center space-x-4 text-sm text-muted-foreground">
          <div className="flex items-center">
            <Badge variant="default" className="h-3 w-3 mr-1" />
            <span>Confirmadas</span>
          </div>
          <div className="flex items-center">
            <Badge variant="secondary" className="h-3 w-3 mr-1" />
            <span>Pendentes</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppointmentCalendar;
